import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Star } from 'lucide-react';

export default function SatisfactionWidget({ satisfaction }) {
    if (!satisfaction) return null;

    return (
        <Card className="bg-slate-900 border-border mb-4 sm:mb-8">
            <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    {/* Score circle */}
                    <div className="flex items-center gap-4">
                        <div className="relative w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0">
                            <svg className="w-16 h-16 sm:w-24 sm:h-24 -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted" />
                                <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeLinecap="round"
                                    strokeDasharray={`${satisfaction.global_score * 2.64} 264`}
                                    className={satisfaction.global_score >= 75 ? 'text-green-400' : satisfaction.global_score >= 50 ? 'text-yellow-400' : 'text-red-400'}
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-lg sm:text-2xl font-bold text-white">{satisfaction.global_score}</span>
                                <span className="text-[8px] sm:text-[10px] text-muted-foreground">/ 100</span>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm sm:text-lg font-bold text-white">Satisfaction</h3>
                            <Badge className={`mt-1 text-[10px] sm:text-xs ${
                                satisfaction.global_score >= 75 ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                satisfaction.global_score >= 50 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                'bg-red-500/20 text-red-400 border-red-500/30'
                            }`}>
                                {satisfaction.label}
                            </Badge>
                            <p className="text-[10px] text-muted-foreground mt-1">{satisfaction.total_mandates || 0} mandats</p>
                        </div>
                    </div>

                    {/* Component bars */}
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                        {satisfaction.components && Object.entries(satisfaction.components).map(([key, comp]) => (
                            <div key={key} className="bg-white/5 rounded-lg p-2 sm:p-3">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[9px] sm:text-[10px] text-muted-foreground truncate">{comp.label}</span>
                                    <span className="text-[10px] sm:text-xs font-bold text-white ml-1">{comp.value}%</span>
                                </div>
                                <div className="h-1 sm:h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${
                                            comp.value >= 75 ? 'bg-green-400' : comp.value >= 50 ? 'bg-yellow-400' : 'bg-red-400'
                                        }`}
                                        style={{ width: `${Math.min(comp.value, 100)}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Per-agent scores */}
                {satisfaction.agents && satisfaction.agents.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border hidden sm:block">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Score par agent</p>
                        <div className="flex flex-wrap gap-3">
                            {satisfaction.agents.map((a) => (
                                <div key={a.agent_id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5">
                                    <div className={`w-2 h-2 rounded-full ${a.score >= 75 ? 'bg-green-400' : a.score >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                                    <span className="text-xs text-white">{a.name}</span>
                                    <span className="text-xs font-bold text-muted-foreground">{a.score}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
